import React, { useState } from "react";
import { Table, Tag, Tooltip, Button, Modal, Typography } from "antd";
import { EyeOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { BenchmarkResult } from "@/lib/api/resources/benchmark";
import { ReferenceViewer } from "./ReferenceViewer";
import JsonView from "@uiw/react-json-view";

const { Paragraph } = Typography;

const isErrored = (r: BenchmarkResult): boolean =>
  !!(r.actualOutput && (r.actualOutput as any).error);

export const formatOutput = (output: any): string => {
  if (!output || typeof output !== "object") return "—";
  if (output.error) return `Error: ${output.error}`;
  if (Array.isArray(output.functionCalls) && output.functionCalls.length > 0) {
    return JSON.stringify(
      output.functionCalls[0]?.arguments ?? output.functionCalls[0],
    );
  }
  if (typeof output.text === "string" && Object.keys(output).length === 1)
    return output.text || "—";
  const values = Object.values(output);
  return values.length === 1 ? String(values[0]) : JSON.stringify(output);
};

interface RunCasesTableProps {
  results: BenchmarkResult[];
  loading?: boolean;
}

export const RunCasesTable: React.FC<RunCasesTableProps> = ({
  results,
  loading,
}) => {
  const [selected, setSelected] = useState<BenchmarkResult | null>(null);

  const columns = [
    {
      title: (
        <Tooltip title="The input data or reference file used for this test case">
          <span className="cursor-help flex items-center gap-1">
            Reference{" "}
            <InfoCircleOutlined className="text-gray-400 text-[10px]" />
          </span>
        </Tooltip>
      ),
      dataIndex: "inputReference",
      key: "inputReference",
      render: (_: any, r: BenchmarkResult) => <ReferenceViewer result={r} />,
    },
    {
      title: (
        <Tooltip title="The actual response generated by the AI agent">
          <span className="cursor-help flex items-center gap-1">
            AI Output{" "}
            <InfoCircleOutlined className="text-gray-400 text-[10px]" />
          </span>
        </Tooltip>
      ),
      key: "actualOutput",
      render: (_: any, r: BenchmarkResult) => {
        const text = formatOutput(r.actualOutput);
        const isSingleLine = !text.includes("\n");
        const displayText =
          isSingleLine && text.length > 255 ? `${text.slice(0, 255)}...` : text;
        return (
          <Paragraph
            className="text-xs block max-w-[240px] text-gray-600 mb-0 whitespace-pre-wrap break-all"
            ellipsis={{ rows: 3, tooltip: text }}
          >
            {displayText}
          </Paragraph>
        );
      },
    },
    {
      title: (
        <Tooltip title="Deterministic exact match verdict against the expected output">
          <span className="cursor-help flex items-center gap-1">
            Verdict <InfoCircleOutlined className="text-gray-400 text-[10px]" />
          </span>
        </Tooltip>
      ),
      key: "correct",
      render: (_: any, r: BenchmarkResult) => {
        if (isErrored(r)) return <Tag color="error">Error</Tag>;
        if (r.correct === true) return <Tag color="success">Match</Tag>;
        if (r.correct === false) return <Tag color="warning">Mismatch</Tag>;
        return <Tag color="default">—</Tag>;
      },
    },
    {
      title: (
        <Tooltip title="AI Judge's score rating the quality of the AI Output (1-10)">
          <span className="cursor-help flex items-center gap-1">
            Judge <InfoCircleOutlined className="text-gray-400 text-[10px]" />
          </span>
        </Tooltip>
      ),
      dataIndex: "qualityScore",
      key: "qualityScore",
      sorter: (a: BenchmarkResult, b: BenchmarkResult) =>
        (a.qualityScore ?? -1) - (b.qualityScore ?? -1),
      render: (score: number | null, r: BenchmarkResult) => {
        if (score == null) return <span className="text-gray-300">—</span>;
        const color =
          score >= 8 ? "#10b981" : score >= 5 ? "#f59e0b" : "#ef4444";
        return (
          <Tooltip
            title={
              <div className="text-xs">
                <div className="font-bold mb-1">Judge Score: {score}/10</div>
                <div>{r.judgeReasoning || "No reasoning provided."}</div>
              </div>
            }
            styles={{ root: { width: "max-content", maxWidth: 300 } }}
          >
            <span
              style={{ color, fontWeight: 700 }}
              className="cursor-help border-b border-dashed border-current pb-0.5"
            >
              {score}/10
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: (
        <Tooltip title="Time taken for the AI to generate the response">
          <span className="cursor-help flex items-center gap-1">
            Latency <InfoCircleOutlined className="text-gray-400 text-[10px]" />
          </span>
        </Tooltip>
      ),
      dataIndex: "latencyMs",
      key: "latencyMs",
      sorter: (a: BenchmarkResult, b: BenchmarkResult) =>
        (a.latencyMs ?? 0) - (b.latencyMs ?? 0),
      render: (ms: number | null) =>
        ms != null ? (
          <Tooltip title={`${ms} milliseconds`}>
            <span className="font-mono text-gray-500 text-xs cursor-help border-b border-dashed border-gray-300 pb-0.5">
              {ms}ms
            </span>
          </Tooltip>
        ) : (
          "—"
        ),
    },
    {
      title: (
        <Tooltip title="Total number of tokens (prompt + completion) used">
          <span className="cursor-help flex items-center gap-1">
            Tokens <InfoCircleOutlined className="text-gray-400 text-[10px]" />
          </span>
        </Tooltip>
      ),
      dataIndex: "tokensUsed",
      key: "tokensUsed",
      sorter: (a: BenchmarkResult, b: BenchmarkResult) =>
        (a.tokensUsed ?? 0) - (b.tokensUsed ?? 0),
      render: (t: number | null, r: BenchmarkResult) =>
        t != null ? (
          <Tooltip
            title={`Prompt: ${r.promptTokens ?? "?"} | Completion: ${r.completionTokens ?? "?"}`}
          >
            <span className="font-mono text-gray-500 text-xs cursor-help border-b border-dashed border-gray-300 pb-0.5">
              {t}
            </span>
          </Tooltip>
        ) : (
          "—"
        ),
    },
    {
      title: (
        <Tooltip title="Estimated API cost for this test case">
          <span className="cursor-help flex items-center gap-1">
            Cost <InfoCircleOutlined className="text-gray-400 text-[10px]" />
          </span>
        </Tooltip>
      ),
      dataIndex: "cost",
      key: "cost",
      sorter: (a: BenchmarkResult, b: BenchmarkResult) =>
        (a.cost ?? 0) - (b.cost ?? 0),
      render: (c: number | null) =>
        c != null ? (
          <Tooltip title="Estimated LLM API cost">
            <span className="font-mono text-gray-500 text-xs cursor-help border-b border-dashed border-gray-300 pb-0.5">
              ${c.toFixed(4)}
            </span>
          </Tooltip>
        ) : (
          "—"
        ),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, r: BenchmarkResult) => (
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => setSelected(r)}
          title="View full output"
        />
      ),
    },
  ];

  return (
    <>
      <Table
        dataSource={results}
        columns={columns as any}
        rowKey="id"
        size="small"
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        scroll={{ x: true }}
      />

      <Modal
        title="Test Case Details"
        open={!!selected}
        onCancel={() => setSelected(null)}
        footer={null}
        width={760}
        destroyOnHidden
      >
        {selected && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto mt-2 pr-1">
            <div>
              <h4 className="font-semibold text-gray-700 mb-1">
                Input Reference
              </h4>
              <div className="bg-gray-100 p-3 rounded text-sm break-all flex items-center h-12">
                <ReferenceViewer result={selected} textClassName="" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-gray-700 mb-1">
                  Expected (Ground Truth)
                </h4>
                <div className="bg-gray-100 p-3 rounded text-sm font-mono whitespace-pre-wrap break-words">
                  {formatOutput(selected.expectedOutput)}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-1">Verdict</h4>
                <div className="bg-gray-100 p-3 rounded text-sm">
                  {isErrored(selected) ? (
                    <Tag color="error">Error</Tag>
                  ) : selected.correct === true ? (
                    <Tag color="success">Match</Tag>
                  ) : selected.correct === false ? (
                    <Tag color="warning">Mismatch</Tag>
                  ) : (
                    <Tag>—</Tag>
                  )}
                  {selected.qualityScore != null && (
                    <span className="ml-2 font-semibold">
                      Judge: {selected.qualityScore}/10
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-700 mb-1">
                Actual Output (JSON)
              </h4>
              <div className="bg-gray-50 border border-gray-100 p-4 rounded-md overflow-auto max-h-[400px] shadow-sm">
                <JsonView
                  value={selected.actualOutput || {}}
                  style={{ backgroundColor: "transparent" }}
                />
              </div>
            </div>
            {selected.judgeReasoning && (
              <div>
                <h4 className="font-semibold text-gray-700 mb-1">
                  Judge Reasoning
                </h4>
                <div className="bg-blue-50 p-4 rounded-md text-sm border border-blue-100 text-blue-900 whitespace-pre-wrap">
                  {selected.judgeReasoning}
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 text-center text-sm">
              <div className="bg-gray-50 rounded p-2">
                <div className="text-gray-400 text-xs uppercase tracking-wide">
                  Latency
                </div>
                <div className="font-mono font-semibold">
                  {selected.latencyMs != null ? `${selected.latencyMs}ms` : "—"}
                </div>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <div className="text-gray-400 text-xs uppercase tracking-wide">
                  Tokens
                </div>
                <div className="font-mono font-semibold">
                  {selected.tokensUsed ?? "—"}
                  {selected.promptTokens != null &&
                    selected.completionTokens != null && (
                      <span className="text-gray-400 font-normal text-xs">
                        {" "}
                        ({selected.promptTokens}/{selected.completionTokens})
                      </span>
                    )}
                </div>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <div className="text-gray-400 text-xs uppercase tracking-wide">
                  Cost
                </div>
                <div className="font-mono font-semibold">
                  {selected.cost != null ? `$${selected.cost.toFixed(4)}` : "—"}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default RunCasesTable;
